import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabaseClient';
import { SuccessCase, SuccessCaseFormData, SuccessCaseStatus } from '../../../../types/successCase';
import ImageUploader from '../../../../components/base/ImageUploader';
import HelpModal, { HelpStep } from '../../../../components/feature/HelpModal';

const SUCCESS_HELP_STEPS: HelpStep[] = [
  {
    icon: 'ri-toggle-line',
    title: '公開・下書きを選ぶ',
    description: '「公開」にすると成婚事例ページに表示されます。まだ準備中の場合は「下書き」を選んで保存しておきましょう。',
    tips: ['下書きはいつでも公開に切り替えられます'],
  },
  {
    icon: 'ri-group-line',
    title: '年齢を入力する',
    description: '成婚されたおふたりの年齢を入力します。記載形式は「67歳 × 64歳」のように「×」で区切ると表示が統一されます。',
    tips: ['年齢は必須項目です', '例：67歳 × 64歳、男性 56歳 ・ 女性 52歳'],
  },
  {
    icon: 'ri-time-line',
    title: '活動期間を入力する',
    description: '入会から成婚までの活動期間を入力します。未入力の場合は空欄になります。',
    tips: ['例：活動期間 約6ヶ月、入会から10ヶ月で成婚'],
  },
  {
    icon: 'ri-sort-asc',
    title: '表示順を設定する',
    description: '数字が小さいほど先に表示されます。最新の成婚事例を一番番先に表示したい場合は数字1を設定しましょう。',
    tips: ['新規追加時は自動で最大値+1になります'],
  },
  {
    icon: 'ri-image-add-line',
    title: '写真をアップロードする',
    description: '成婚事例カードに表示される画像です。「画像を選択」ボタンからPNG・JPG・WEBPファイルをアップロードしてください。',
    tips: ['推奨サイズ：正方形 or 縦長画像', 'お客様のプライバシーに配慮しながら写真を選んでください'],
  },
  {
    icon: 'ri-chat-quote-line',
    title: 'コメントを入力する',
    description: 'お客様の実际の声・感想や、成婚の経緯を入力します（500文字以内）。読者が共感できるリアルなコメントが大切です。',
    tips: ['コメントは必須項目です', '個人が特定されないような表現を心がけましょう'],
  },
  {
    icon: 'ri-save-line',
    title: '保存する',
    description: '右下の「公開して保存」または「下書きとして保存」ボタンをクリックして保存します。保存後すぐに成婚事例ページに反映されます。',
    tips: ['保存後もいつでも編集・削除できます'],
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: SuccessCase | null;
  onSaved: () => void;
  nextSortOrder: number;
}

const makeEmpty = (nextOrder: number): SuccessCaseFormData => ({
  active_period: '',
  ages: '',
  excerpt: '',
  image_url: '',
  status: 'published',
  sort_order: nextOrder,
});

export default function SuccessCaseFormModal({ isOpen, onClose, item, onSaved, nextSortOrder }: Props) {
  const [form, setForm] = useState<SuccessCaseFormData>(makeEmpty(nextSortOrder));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setForm({
        active_period: item.active_period,
        ages: item.ages,
        excerpt: item.excerpt,
        image_url: item.image_url ?? '',
        status: item.status,
        sort_order: item.sort_order,
      });
    } else {
      setForm(makeEmpty(nextSortOrder));
    }
    setError(null);
  }, [isOpen, item, nextSortOrder]);

  const handleChange = (field: keyof SuccessCaseFormData, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = (status: SuccessCaseStatus) => {
    setForm((prev) => ({ ...prev, status }));
  };

  const handleSave = async () => {
    if (!form.ages.trim()) {
      setError('年齢を入力してください。');
      return;
    }
    if (!form.excerpt.trim()) {
      setError('コメントを入力してください。');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      active_period: form.active_period.trim(),
      ages: form.ages.trim(),
      excerpt: form.excerpt.trim(),
      image_url: form.image_url.trim() || null,
      status: form.status,
      published: form.status === 'published',
      sort_order: Number(form.sort_order) || 0,
      updated_at: new Date().toISOString(),
    };

    let err;
    if (item) {
      ({ error: err } = await supabase
        .from('8a4f04ed_success_cases')
        .update(payload)
        .eq('id', item.id));
    } else {
      ({ error: err } = await supabase
        .from('8a4f04ed_success_cases')
        .insert(payload));
    }

    setSaving(false);
    if (err) {
      setError('保存に失敗しました。もう一度お試しください。');
      return;
    }
    onSaved();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <HelpModal
        title="成婚事例の編集ガイド"
        sections={[{ label: '手順', steps: SUCCESS_HELP_STEPS }]}
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl"
        style={{ background: '#fff', border: '1px solid #e8d8cc' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 rounded-t-2xl"
          style={{ borderBottom: '1px solid #e8d8cc', background: '#fff8f2' }}
        >
          <h2 className="font-bold text-lg" style={{ color: '#4a3728' }}>
            {item ? '成婚事例を編集' : '新規成婚事例を追加'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHelpOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-opacity hover:opacity-80 whitespace-nowrap"
              style={{ background: '#fff0d8', color: '#c4903a', border: '1px solid #f0d8a0' }}
            >
              <span className="flex items-center justify-center w-3.5 h-3.5">
                <i className="ri-question-line text-xs"></i>
              </span>
              使い方
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer"
              style={{ color: '#9b8b7f' }}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="p-6 flex flex-col gap-5">
          {error && (
            <div
              className="px-4 py-3 rounded-lg text-sm font-bold"
              style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}
            >
              {error}
            </div>
          )}

          {/* Status Toggle */}
          <div
            className="flex items-center justify-between px-4 py-3 rounded-xl"
            style={{
              background: form.status === 'published' ? '#f2f8f4' : '#fdf5e8',
              border: `1.5px solid ${form.status === 'published' ? '#c8e0cf' : '#f0d8a0'}`,
            }}
          >
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5">
                <i
                  className={`text-sm ${form.status === 'published' ? 'ri-global-line' : 'ri-draft-line'}`}
                  style={{ color: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}
                ></i>
              </span>
              <span className="text-sm font-bold" style={{ color: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}>
                {form.status === 'published' ? '公開中' : '下書き保存'}
              </span>
              <span className="text-xs" style={{ color: '#9b8b7f' }}>
                {form.status === 'published' ? '— 成婚事例ページに表示されます' : '— 成婚事例ページには表示されません'}
              </span>
            </div>
            <div className="flex rounded-full p-0.5" style={{ background: '#e8d8cc' }}>
              <button
                type="button"
                onClick={() => handleStatusChange('published')}
                className="px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: form.status === 'published' ? '#5a9e6f' : 'transparent',
                  color: form.status === 'published' ? '#fff' : '#9b8b7f',
                }}
              >
                公開
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange('draft')}
                className="px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: form.status === 'draft' ? '#c4903a' : 'transparent',
                  color: form.status === 'draft' ? '#fff' : '#9b8b7f',
                }}
              >
                下書き
              </button>
            </div>
          </div>

          {/* Ages & Active Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
                年齢 <span style={{ color: '#cc4444' }}>*</span>
              </label>
              <input
                type="text"
                value={form.ages}
                onChange={(e) => handleChange('ages', e.target.value)}
                placeholder="例：67歳 × 64歳"
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
                活動期間
              </label>
              <input
                type="text"
                value={form.active_period}
                onChange={(e) => handleChange('active_period', e.target.value)}
                placeholder="例：活動期間 約6ヶ月"
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              表示順（数字が小さいほど先に表示）
            </label>
            <input
              type="number"
              min={1}
              value={form.sort_order}
              onChange={(e) => handleChange('sort_order', Number(e.target.value))}
              className="w-32 px-4 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              写真
            </label>
            <ImageUploader
              currentUrl={form.image_url}
              onChange={(url) => handleChange('image_url', url)}
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              コメント <span style={{ color: '#cc4444' }}>*</span>
            </label>
            <textarea
              value={form.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              rows={5}
              maxLength={500}
              placeholder="お客様の声・コメントを入力（500文字以内）"
              className="w-full px-4 py-2.5 rounded-lg text-sm outline-none resize-none"
              style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728', lineHeight: '1.7' }}
            />
            <p className="text-right text-xs mt-1" style={{ color: '#9b8b7f' }}>
              {form.excerpt.length} / 500文字
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 rounded-b-2xl"
          style={{ borderTop: '1px solid #e8d8cc', background: '#fff8f2' }}
        >
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#f0e8e0', color: '#6b5a4e' }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-60"
            style={{ background: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}
          >
            {saving ? (
              <>
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                </span>
                保存中...
              </>
            ) : (
              <>
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-save-line text-sm"></i>
                </span>
                {item
                  ? form.status === 'published' ? '公開して保存' : '下書きとして保存'
                  : form.status === 'published' ? '公開して追加' : '下書きとして追加'
                }
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}